import { configureStore, combineReducers } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  createMigrate,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  type PersistMigrate,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import sessionStorage from 'redux-persist/lib/storage/session';
import { themeReducer } from '@kss/theme';
import { authReducer } from '@kss/auth';
import { sidebarReducer } from '@kss/layouts';
import { dineReducer } from './dine/dineSlice';

/**
 * Bump this whenever the desired-default of a persisted slice changes
 * (e.g. swapping the storefront from light/default to dark/blue). The
 * `migrate` map below resets stale slices on rehydrate so existing
 * users pick up the new defaults without having to clear localStorage.
 */
const PERSIST_VERSION = 4;

interface MigrationState {
  theme?: { mode?: string; colorTheme?: string; colorMode?: string };
  auth?: unknown;
}

/**
 * **Two storage targets on purpose.**
 *
 *   - `theme`  → **localStorage**   — user-chosen dark/light + brand
 *     colour MUST survive a browser restart; it's a UX preference,
 *     not a credential.
 *   - `auth`   → **sessionStorage** — JWT + refresh token are scoped
 *     to the current browser TAB. Closing the tab (or quitting the
 *     browser) clears the token, forcing a fresh sign-in next time.
 *     Mirrors how every modern banking / financial app treats auth.
 *     (Bumped PERSIST_VERSION to v3 alongside this change so any
 *     leftover localStorage auth blob from before is dropped.)
 *   - `sidebar` → not persisted at all; rehydrates from defaults.
 */
const authPersistConfig = {
  key: 'kss-web-auth',
  storage: sessionStorage,
  version: PERSIST_VERSION,
};

const persistConfig = {
  key: 'kss-web',
  storage,
  version: PERSIST_VERSION,
  // auth handled by its own per-slice persistReducer below — keep
  // it out of the root localStorage write so the JWT can never
  // accidentally leak there.
  whitelist: ['theme'],
  migrate: createMigrate(
    {
      // v2 — switch storefront defaults to dark + blue.
      2: (state) => {
        if (!state) return state;
        const next = { ...(state as unknown as MigrationState) };
        delete next.theme;
        return next as typeof state;
      },
      // v3 — auth moved out of localStorage. Drop any legacy auth
      // blob that was previously persisted to the `kss-web` root key.
      3: (state) => {
        if (!state) return state;
        const next = { ...(state as unknown as MigrationState) };
        delete next.auth;
        return next as typeof state;
      },
      // v4 — default theme switched to LIGHT. Drop any persisted theme so
      // users still on the old dark default pick up the new light default.
      4: (state) => {
        if (!state) return state;
        const next = { ...(state as unknown as MigrationState) };
        delete next.theme;
        return next as typeof state;
      },
    },
    { debug: false },
  ) as PersistMigrate,
};

/**
 * Dine-in cart / table session lives in **sessionStorage** so it
 * survives navigation between Menu / Orders / Pay Bill tabs and full
 * page reloads, but is cleared when the customer closes the tab —
 * different customers using the same table QR shouldn't share a cart.
 */
const dinePersistConfig = {
  key: 'kss-web-dine',
  storage: sessionStorage,
  version: PERSIST_VERSION,
};

const rootReducer = combineReducers({
  theme: themeReducer,
  sidebar: sidebarReducer,
  auth: persistReducer(authPersistConfig, authReducer),
  dine: persistReducer(dinePersistConfig, dineReducer),
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefault) =>
    getDefault({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
